import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputBase,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Avatar,
  Chip,
  Menu,
} from "@mui/material";
import {
  SearchOutlined,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import Button from "../../components/common/Button";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import AddUserForm from "./AddUserForm";
import EditUserDialog from "./EditUserDialog";
import ConfirmDeleteUserDialog from "./ConfirmDeleteUserDialog";
import DataGrid from "../../components/common/CustomeDataGrid";
import type { User } from "../../store/slice/user-management/types";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchUsers, deleteUser } from "../../store/slice/user-management/usersThunks";
import { fetchContentTypes, fetchGroups, fetchPermissions } from "../../store/slice/rolesAndPermissions/thunks";
import apiService from "../../service/Api";

export default function UserManagement() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { users, loading, error } = useAppSelector((state) => state.users);

  /* -------------------- LOCAL STATE -------------------- */
  const [inputValue, setInputValue] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [userFormOpen, setUserFormOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const userRoles = [
    {
      title: "Admin",
      value: "admin"
    },
    {
      title: "Staff",
      value: "staff"
    },
    {
      title: "Crew",
      value: "crew"
    },
    {
      title: "Client",
      value: "client"
    },
    {
      title: "Funeral Director",
      value: "funeral_director"
    }
  ]
  const [paginationModel, setPaginationModel] =
    useState<GridPaginationModel>({
      page: 0,
      pageSize: 100,
    });

  /* -------------------- FETCH USERS -------------------- */
  useEffect(() => {
    dispatch(fetchUsers());
    //fetch permissions/groups/content types  
    dispatch(fetchPermissions());
    dispatch(fetchGroups());
    dispatch(fetchContentTypes())
  }, [dispatch]);

  /* -------------------- SEARCH DEBOUNCE -------------------- */
  useEffect(() => {



    setAppliedSearchTerm(inputValue.toLowerCase());
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

  }, [inputValue]);

  /* -------------------- FILTER LOGIC -------------------- */
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(appliedSearchTerm) ||
        user.email.toLowerCase().includes(appliedSearchTerm)


      const matchesRole =
        roleFilter === "all" || user.role === roleFilter;

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && user.is_active) ||
        (statusFilter === "suspended" && !user.is_active);

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, appliedSearchTerm, roleFilter, statusFilter]);

  /* -------------------- HANDLERS -------------------- */
  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    userId: number
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUserId(null);
  };

  const findSelectedUser = () =>
    users.find((u) => u.id === selectedUserId) ?? null;

  const handleRowClick = (params: { row: User }) => {
    navigate(`/user-management/view/${params.row.id}`);
  };

  const handleEditUser = () => {
    setSelectedUser(findSelectedUser());
    setEditDialogOpen(true);
    handleMenuClose();
  };


  const handleDeleteUser = () => {
    setSelectedUser(findSelectedUser());
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDeleteUser = async () => {
    

    const user = selectedUser
    
    if (!user) return toast.error("Could not delete the user");;
    
    await dispatch(deleteUser(user.id)).unwrap();
    toast.success("User deleted");
    setDeleteDialogOpen(false);
    setSelectedUserId(null);
  };

  const handleCancelDeleteUser = () => {
    setDeleteDialogOpen(false);
  };

  /* -------------------- CRUD -------------------- */
 

  const handleSaveUser = async (formData: FormData) => {
    try {
      await apiService.post("/api/authentication/users/", formData, false);
      toast.success("User created successfully");
      dispatch(fetchUsers());
      setUserFormOpen(false);
    } catch (error) {
      const errorDetail = (error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      if (
        errorDetail &&
        errorDetail.includes("'NoneType' object has no attribute 'seek'")
      ) {
        toast.success("User created successfully");
        dispatch(fetchUsers());
        setUserFormOpen(false);
      } else {
        toast.error("Failed to create user");
        console.error("There was an error creating the user!", error);
      }
    }
  };

  const handleSaveEditUser = async (formData: FormData) => {
    try {
      if (!selectedUser) return;
      console.log("Logging form data : ",formData);
      await apiService.patch(`/api/authentication/users/${selectedUser.id}/`, formData, false);
      dispatch(fetchUsers());
      setEditDialogOpen(false);
      toast.success("User updated successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (err) {
      let errorMessage = "Failed to update user. Please try again.";
      const errorDetail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      if (errorDetail) {
        errorMessage = errorDetail;
      }
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Update user error:", err);
    }
  };
  

  const backgroundColor = "#A9B2C3";

  /* -------------------- COLUMNS -------------------- */
  const columns: GridColDef<User>[] = [
    {
      field: "full_name",
      headerName: "Full Name",
      flex: 1,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={2} sx={{ padding:1, borderRadius:2}}>
          <Avatar sx={{ width: 40, height: 40 }} src={params.row.profile_picture_url || undefined}>
            {!params.row.profile_picture_url && <PersonIcon sx={{ fontSize: 35, color: 'grey.100' }} />}
          </Avatar>
          <Typography>
            {params.row.first_name} {params.row.last_name}
          </Typography>
        </Box>
      ),
    },
    { field: "email", headerName: "Email", flex: 1 },
    { field: "phone_number", headerName: "Phone", flex: 1 },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      renderCell: (params) => <Chip label={params.row.role} size="small" />,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.row.is_active ? "Active" : "Suspended"}
          color={params.row.is_active ? "success" : "warning"}
        />
      ),
    },
    {
      field: "2fa",
      headerName: "2FA",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.row.is_2fa_enabled ? "Allowed" : "Disabled"}
          color={params.row.is_2fa_enabled ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuClick(e, params.row.id)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  if (error && !users.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box>
        <Box  display="flex" gap="40px" alignItems="center"  sx={{flex:1}}>
          <Box display="flex" gap="4px" alignItems="center" sx={{flex:1}}>
            <Box
              sx={{
                borderRadius: 3,
                borderColor: "#9c6644",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                pl: 1,
                flex: 1,
                minWidth: 320,
              }}
            >
              <SearchOutlined />
              <InputBase
                placeholder="Search by name or email..."
                sx={{ ml: 1, flex: 1, color: backgroundColor,padding:0.5 }}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
            </Box>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Role</InputLabel>
              <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                {userRoles.map((role) => {
                  return (
                    <MenuItem key={role.value} value={role.value}>
                      {role.title}
                    </MenuItem>
                  );
                })}



              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </Select>
            </FormControl>


          </Box>

          <Box    sx={{flexDirection:"row", alignItems:"center", justifyContent:"flex-end",}}>
            <Button size="small" variant="contained" sx={{
              padding: 1
            }} onClick={() => setUserFormOpen(true)}>
              Add New User
            </Button>
          </Box>
        </Box>

        <Paper sx={{ p: 3, mt: 2 }}>
          {loading && filteredUsers.length <0 ? (
            <CircularProgress />
          ) : (
            <DataGrid
              rows={filteredUsers}
              columns={columns}
              rowCount={filteredUsers.length}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              onRowClick={handleRowClick}
            />
          )}
        </Paper>


        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleMenuClose}>
          <MenuItem onClick={handleEditUser}>
            <EditIcon sx={{ mr: 1 }} /> Edit User
          </MenuItem>
          <MenuItem onClick={handleDeleteUser}>
            <DeleteIcon sx={{ mr: 1 }} /> Delete User
          </MenuItem>
        </Menu>

       {
        selectedUser &&  <ConfirmDeleteUserDialog
          open={deleteDialogOpen}
          onClose={handleCancelDeleteUser}
          onConfirm={handleConfirmDeleteUser}
          userName={selectedUser.first_name + " " + selectedUser.last_name}
        />
       }

        <AddUserForm
          open={userFormOpen}
          onClose={() => setUserFormOpen(false)}
          onSave={handleSaveUser}
        />

        {selectedUser && (
          <EditUserDialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            onSave={handleSaveEditUser}
            selectedUser={selectedUser}
          />
        )}
      </Box>
    </motion.div>
  );
}
